// Styles for the Chrome side-panel embed (WEB-7101). Injected as a <style> inside the
// ShadcnScope shadow DOM — global SCSS can't reach a shadow root, so this mirrors the
// workspaceStyles / praxisStyles pattern. Keep embed styling here, not inline in the TSX.
const embedStyles = `
/* Collapse the .ai-ws sidebar grid column (the embed has no sidebar) and keep the chat
   from overflowing the narrow panel. */
.ai-ws-embed { grid-template-columns: 1fr; }
.ai-ws-embed,
.ai-ws-embed .ws-main,
.ai-ws-embed .ws-main-inner,
.ai-ws-embed .ws-content-row,
.ai-ws-embed .ws-chat-col,
.ai-ws-embed .ws-chat-body,
.ai-ws-embed .ws-chat-body-inner {
	min-width: 0;
	max-width: 100%;
	/* Let these grid/flex items shrink to their track instead of growing to
	   content height — required so .embed-add can bound its inner scroller. */
	min-height: 0;
	/* clip, not hidden — hidden boxes are still programmatically scrollable, so a stray
	   scrollIntoView/focus would permanently shift these containment boxes. */
	overflow: hidden;
	overflow: clip;
}
.ai-ws-embed .ws-main-inner { position: relative; }
.ai-ws-embed .ws-chat-body-inner { padding-left: 16px; padding-right: 16px; }
.ai-ws-embed .ws-chat-bubble-agent, .ai-ws-embed .ws-chat-bubble-me { overflow-wrap: anywhere; }
.ai-ws-embed .ws-chat-bubble-agent pre, .ai-ws-embed .ws-chat-bubble-agent code {
	white-space: pre-wrap;
	word-break: break-word;
	max-width: 100%;
}

/* Top bar with the burger menu */
.embed-topbar {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding: 4px 8px;
	border-bottom: 1px solid var(--ws-line);
	background: var(--ws-panel);
	flex: 0 0 auto;
}
.embed-icon-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: none;
	background: transparent;
	color: var(--ws-ink-3);
	cursor: pointer;
	border-radius: var(--ws-r-sm, 6px);
}
.embed-icon-btn:hover {
	background: var(--ws-soft);
	color: var(--ws-ink-2);
}
.embed-icon-btn svg {
	width: 18px;
	height: 18px;
}

/* Empty-state (fresh chat): logo + tagline centred above the composer */
.embed-empty {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	pointer-events: none;
	/* Bottom padding offsets the composer so the logo sits in the visible area above it. */
	padding: 24px 24px 120px;
	text-align: center;
}
.embed-empty-logo {
	margin-bottom: 16px;
}
.embed-empty-logo svg {
	width: 150px;
	height: auto;
	display: block;
}
.embed-empty-tagline {
	max-width: 260px;
	font-family: var(--ws-sans);
	font-size: 13px;
	line-height: 1.5;
	color: var(--ws-ink-4);
}

/* Slide-in conversation drawer */
.embed-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.3);
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.2s ease;
	z-index: 1000;
}
.embed-backdrop.is-open {
	opacity: 1;
	pointer-events: auto;
}
.embed-drawer {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 280px;
	max-width: 85%;
	background: var(--ws-panel);
	border-left: 1px solid var(--ws-line);
	box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
	transform: translateX(100%);
	transition: transform 0.2s ease;
	z-index: 1001;
	display: flex;
	flex-direction: column;
	font-family: var(--ws-sans);
}
.embed-drawer.is-open {
	transform: translateX(0);
}
.embed-drawer-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 12px;
	border-bottom: 1px solid var(--ws-line);
}
.embed-drawer-title {
	font-weight: 600;
	font-size: 14px;
	color: var(--ws-ink);
}
.embed-new-chat {
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 10px 12px;
	padding: 8px 12px;
	border: 1px solid var(--ws-line);
	border-radius: var(--ws-r-sm, 6px);
	background: var(--ws-soft);
	color: var(--ws-ink);
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
}
.embed-new-chat:hover {
	background: var(--ws-hover);
}
.embed-new-chat svg {
	width: 16px;
	height: 16px;
}
.embed-conv-list {
	flex: 1;
	overflow-y: auto;
	padding: 0 8px 12px;
}
.embed-conv-item {
	display: block;
	width: 100%;
	text-align: left;
	padding: 8px 10px;
	border: none;
	border-radius: var(--ws-r-sm, 6px);
	background: transparent;
	color: var(--ws-ink-2);
	font-size: 13px;
	cursor: pointer;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.embed-conv-item:hover {
	background: var(--ws-soft);
}
.embed-conv-item.is-active {
	background: var(--ws-hover);
}

/* No-access fallback */
.embed-no-access {
	padding: 32px;
	max-width: 360px;
	margin: 64px auto;
	text-align: center;
	font-family: var(--ws-sans);
}
.embed-no-access-logo {
	margin-bottom: 20px;
}
.embed-no-access-logo svg {
	width: 130px;
	height: auto;
	display: block;
	margin: 0 auto;
}
.embed-no-access-title {
	font-size: 15px;
	font-weight: 600;
	color: var(--ws-ink);
	margin-bottom: 8px;
}
.embed-no-access-body {
	font-size: 13px;
	line-height: 1.5;
	color: var(--ws-ink-4);
}

/* ── Add to Upsales tab (WEB-7280) ─────────────────────────────────── */
.embed-tabbar {
	flex: 0 0 auto;
	display: flex;
	border-top: 1px solid var(--ws-line);
	background: var(--ws-panel);
}
.embed-tab {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 8px;
	border: none;
	background: none;
	cursor: pointer;
	font-size: 12px;
	font-family: var(--ws-sans);
	color: var(--ws-ink-4);
}
.embed-tab.is-active {
	color: var(--ws-ink);
	font-weight: 600;
}
.embed-tab svg {
	width: 15px;
	height: 15px;
}
.embed-tab-label {
	max-width: 110px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.embed-tab-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: var(--ws-info);
}
.ai-ws-embed .ws-main-inner.is-hidden {
	display: none;
}
.embed-add {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	font-family: var(--ws-sans);
}
.embed-add-scroll {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
}
.embed-add-status {
	flex: 1;
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: center;
	justify-content: center;
	color: var(--ws-ink-4);
	font-size: 13px;
}
.embed-add-error {
	font-size: 12px;
	color: var(--ws-err);
}
.embed-add-form {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}
.embed-add-field {
	display: flex;
	flex-direction: column;
	gap: 3px;
	flex: 1 1 100%;
	min-width: 0;
}
.embed-add-field.is-half {
	flex: 1 1 calc(50% - 4px);
}
.embed-add-field > span {
	font-size: 11px;
	font-weight: 600;
	color: var(--ws-ink-4);
}
.embed-add-field input {
	padding: 6px 8px;
	border: 1px solid var(--ws-line-2);
	border-radius: var(--ws-r-sm, 6px);
	font-size: 13px;
	font-family: var(--ws-sans);
	color: var(--ws-ink);
	background: var(--ws-bg);
	min-width: 0;
}
.embed-add-field input:focus {
	outline: none;
	border-color: var(--ws-accent);
}
.embed-add-field input:disabled {
	opacity: 0.6;
}
.embed-add-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: none;
	border-radius: var(--ws-r-sm, 6px);
	background: var(--ws-accent);
	color: #fff;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;
}
.embed-add-btn:disabled {
	opacity: 0.6;
	cursor: default;
}
.embed-add-btn svg {
	width: 14px;
	height: 14px;
}
.embed-add-btn-primary {
	width: 100%;
	justify-content: center;
	padding: 10px 12px;
	font-size: 13px;
}
.embed-add-contacts {
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.embed-add-contacts-heading {
	font-size: 11px;
	font-weight: 600;
	color: var(--ws-ink-4);
	text-transform: uppercase;
	letter-spacing: 0.04em;
}
.embed-add-item {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
	width: 100%;
	border: 1px solid var(--ws-line);
	border-radius: var(--ws-r-md, 10px);
	background: none;
	cursor: pointer;
	text-align: left;
	font-family: var(--ws-sans);
}
.embed-add-item:hover:not(:disabled) {
	background: var(--ws-hover);
}
.embed-add-item:disabled {
	cursor: default;
	opacity: 0.7;
}
.embed-add-item-icon {
	width: 20px;
	height: 20px;
	color: var(--ws-ink-4);
	flex: 0 0 auto;
}
.embed-add-item-info {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
}
.embed-add-item-name {
	font-size: 13px;
	font-weight: 600;
	color: var(--ws-ink);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.embed-add-item-meta {
	font-size: 12px;
	color: var(--ws-ink-4);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.embed-add-item-action {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	background: var(--ws-accent);
	color: #fff;
	flex: 0 0 auto;
}
.embed-add-item-action svg {
	width: 15px;
	height: 15px;
}
.embed-add-item-action.is-saving {
	opacity: 0.5;
}
.embed-add-item-done {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	color: var(--ws-ok);
	flex: 0 0 auto;
}
.embed-add-item-done svg {
	width: 15px;
	height: 15px;
}
.embed-add-item-done svg:last-child:not(:first-child) {
	color: var(--ws-ink-4);
}

/* In-panel modal (company/contact details) */
.embed-modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(15, 30, 45, 0.35);
	z-index: 1100;
}
.embed-modal {
	position: fixed;
	left: 12px;
	right: 12px;
	top: 15%;
	z-index: 1101;
	background: var(--ws-panel);
	border-radius: var(--ws-r-lg, 14px);
	box-shadow: var(--ws-shadow-md);
	padding: 14px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	font-family: var(--ws-sans);
}
.embed-modal-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
}
.embed-modal-title {
	font-size: 14px;
	font-weight: 600;
	color: var(--ws-ink);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
`;

export default embedStyles;
